<script setup>
import ServerActionsCell from "@frontJs/components/tables/cells/projectManagement/dev/ServerActionsCell.vue";
import LoadingSpinner from "@js/components/LoadingSpinner.vue";
import DataTableLayout from "@js/components/tables/DataTableLayout.vue";
import CrossIconCell from "@js/components/tables/cells/CrossIconCell.vue";
import LinkCellV2 from "@js/components/tables/cells/LinkCellV2.vue";
import PriceCellV2 from "@js/components/tables/cells/PriceCellV2.vue";
import TextCellV2 from "@js/components/tables/cells/TextCellV2.vue";
import { useDataTable } from "@js/utils/dataTable";
import { useRoute } from "@js/utils/route";
import { createColumnHelper } from "@tanstack/vue-table";
import { h } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const { userSessionKey } = defineProps({
  userSessionKey: {
    type: String,
    required: false,
    default: null,
  },
});

const { route } = useRoute();

const columnHelper = createColumnHelper();

const {
  loading,
  table,
  count,
  globalFilter,
  filters,
  availableFilters,
  setGlobalFilter,
  setFilters,
  updateData,
  globalData,
} = useDataTable({
  columns: [
    columnHelper.accessor(() => null, {
      id: "name",
      header: () => t("label.name"),
      cell: (props) =>
        h(LinkCellV2, {
          text: props.row.original.name,
          href: route("front.projectManagement.dev.servers", {
            tab: props.row.original.name.toLowerCase().replace(/\s/g, ""),
          }),
          inertia: true,
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "serverName",
      header: () => t("label.serverName"),
      cell: (props) =>
        props.row.original.serverName
          ? h(TextCellV2, {
              text: props.row.original.serverName,
            })
          : h(CrossIconCell),
    }),
    columnHelper.accessor(() => null, {
      id: "costExcludedTaxPerMonth",
      header: () => t("label.serverCostExcludedTaxPerMonth"),
      cell: (props) =>
        props.row.original.costExcludedTaxPerMonth
          ? h(PriceCellV2, {
              price: props.row.original.costExcludedTaxPerMonth,
            })
          : h(CrossIconCell),
      footer: () =>
        h(PriceCellV2, {
          price: globalData.value.total.serverCostExcludedTaxPerMonth,
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "ipBlockCostExcludedTaxPerMonth",
      header: () => t("label.ipBlockCostExcludedTaxPerMonth"),
      cell: (props) =>
        props.row.original.ipBlockCostExcludedTaxPerMonth
          ? h(PriceCellV2, {
              price: props.row.original.ipBlockCostExcludedTaxPerMonth,
            })
          : h(CrossIconCell),
      footer: () =>
        h(PriceCellV2, {
          price: globalData.value.total.ipBlockCostExcludedTaxPerMonth,
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "maintenanceCostExcludedTaxPerMonth",
      header: () => t("label.maintenanceCostExcludedTaxPerMonth"),
      cell: (props) =>
        props.row.original.maintenanceCostExcludedTaxPerMonth
          ? h(PriceCellV2, {
              price: props.row.original.maintenanceCostExcludedTaxPerMonth,
            })
          : h(CrossIconCell),
      footer: () =>
        h(PriceCellV2, {
          price: globalData.value.total.maintenanceCostExcludedTaxPerMonth,
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "totalCloudflareCostExcludedTaxPerMonth",
      header: () => t("label.totalCloudflareCostExcludedTaxPerMonth"),
      cell: (props) =>
        props.row.original.totalCloudflareCostExcludedTaxPerMonth
          ? h(PriceCellV2, {
              price: props.row.original.totalCloudflareCostExcludedTaxPerMonth,
            })
          : h(CrossIconCell),
      footer: () =>
        h(PriceCellV2, {
          price: globalData.value.total.totalCloudflareCostExcludedTaxPerMonth,
        }),
      enableSorting: false,
    }),
    columnHelper.accessor(() => null, {
      id: "totalCostExcludedTaxPerMonth",
      header: () => t("label.totalCostExcludedTaxPerMonth"),
      cell: (props) =>
        props.row.original.totalCostExcludedTaxPerMonth
          ? h(PriceCellV2, {
              price: props.row.original.totalCostExcludedTaxPerMonth,
            })
          : h(CrossIconCell),
      footer: () =>
        h(PriceCellV2, {
          price: globalData.value.total.totalCostExcludedTaxPerMonth,
        }),
      enableSorting: false,
    }),
    columnHelper.accessor(() => null, {
      id: "totalPricePerMonth",
      header: () => t("label.cashedExcludedTaxPerMonth"),
      cell: (props) =>
        props.row.original.totalPricePerMonth
          ? h(PriceCellV2, {
              price: props.row.original.totalPricePerMonth,
            })
          : h(CrossIconCell),
      footer: () =>
        h(PriceCellV2, {
          price: globalData.value.total.totalPricePerMonth,
        }),
      enableSorting: false,
    }),
    columnHelper.display({
      id: "actions",
      header: t("label.actions"),
      cell: (innerProps) =>
        h(ServerActionsCell, {
          row: innerProps.row,
          callback: updateData,
        }),
    }),
  ],
  url: route("front.projectManagement.dev.postServers"),
  columnPinning: {
    right: ["actions"],
  },
  userSessionKey: userSessionKey,
});
</script>

<template>
  <LoadingSpinner v-if="loading" />

  <div v-else>
    <DataTableLayout
      :table="table"
      :count="count"
      :global-filter="globalFilter"
      :filters="filters"
      :available-filters="availableFilters"
      :set-filters="setFilters"
      :set-global-filter="setGlobalFilter"
      with-footer
    />
  </div>
</template>
